import { useEffect, useMemo, useRef, useState } from 'react';
import { TileLayer, useMap } from 'react-leaflet';
import { isGoogleBasemap, type BasemapId, type MapSettings } from '@geotracker/contracts';

/**
 * The basemaps the live map can draw, and how to get tiles for each.
 *
 * Two are unconditional and need no credentials, so the map is never blank:
 * OpenStreetMap for streets, and Esri's World Imagery for satellite. The Google
 * options light up only when a browser key has been saved in Settings.
 *
 * Google tiles come from the **Map Tiles API**, not from scraping `mt?.google`
 * tile URLs. Those URLs are widely copied around and they are a licence
 * violation — Google's terms permit their imagery in a third-party renderer
 * only through this API or the Maps JavaScript API. The Map Tiles API is the
 * one that fits Leaflet: create a session, then fetch ordinary {z}/{x}/{y}
 * rasters with the session token on the query string.
 */

export interface BasemapDefinition {
  id: BasemapId;
  label: string;
  /** Grouping for the switcher. */
  group: 'Free' | 'Google';
  /** True for photographic imagery, which must NOT get the dark-mode invert. */
  imagery: boolean;
  /** Deepest zoom the provider actually serves; above it tiles are upscaled. */
  maxNativeZoom: number;
  needsGoogleKey: boolean;
}

/**
 * OpenStreetMap, listed first and named separately so it can be the fallback
 * without a non-null assertion on an array lookup.
 */
const OSM: BasemapDefinition = {
  id: 'osm',
  label: 'Streets',
  group: 'Free',
  imagery: false,
  // OSM's standard style stops at 19. Asking for 20 returns 404s, and Leaflet
  // renders those as grey holes rather than upscaling — which is why
  // maxNativeZoom exists and why every entry here declares an honest one.
  maxNativeZoom: 19,
  needsGoogleKey: false,
};

export const BASEMAPS: readonly BasemapDefinition[] = [
  OSM,
  {
    id: 'satellite',
    label: 'Satellite',
    group: 'Free',
    imagery: true,
    maxNativeZoom: 19,
    needsGoogleKey: false,
  },
  {
    id: 'google_roadmap',
    label: 'Google Streets',
    group: 'Google',
    imagery: false,
    maxNativeZoom: 21,
    needsGoogleKey: true,
  },
  {
    id: 'google_satellite',
    label: 'Google Satellite',
    group: 'Google',
    imagery: true,
    maxNativeZoom: 21,
    needsGoogleKey: true,
  },
  {
    id: 'google_hybrid',
    // The one worth having for this product: imagery WITH road names and place
    // labels, so a pin outside a building can be read as "the Taj, not the car
    // park next door" without leaving the map.
    label: 'Google Hybrid',
    group: 'Google',
    imagery: true,
    maxNativeZoom: 21,
    needsGoogleKey: true,
  },
  {
    id: 'google_terrain',
    label: 'Google Terrain',
    group: 'Google',
    imagery: false,
    maxNativeZoom: 21,
    needsGoogleKey: true,
  },
] as const;

export const basemapById = (id: BasemapId): BasemapDefinition =>
  BASEMAPS.find((b) => b.id === id) ?? OSM;

/** Which basemaps this deployment can actually draw right now. */
export function availableBasemaps(hasGoogleKey: boolean): readonly BasemapDefinition[] {
  return BASEMAPS.filter((b) => !b.needsGoogleKey || hasGoogleKey);
}

/**
 * Falls back rather than showing an empty map.
 *
 * The stored default can become undrawable at any time — the key is removed,
 * the billing account lapses — and a saved preference must never be the reason
 * an admin opens the map to a grey rectangle.
 */
export function resolveBasemap(preferred: BasemapId, hasGoogleKey: boolean): BasemapId {
  if (isGoogleBasemap(preferred) && !hasGoogleKey) return 'osm';
  return preferred;
}

// ---------------------------------------------------------------------------
// Google Map Tiles API
// ---------------------------------------------------------------------------

interface GoogleSession {
  session: string;
  /** Epoch seconds. Sessions last ~2 weeks; we refresh well before. */
  expiry: number;
}

type SessionKey = string;

/**
 * Sessions, cached for the life of the page.
 *
 * Creating one is a billable request and every basemap toggle would otherwise
 * make another. Keyed by everything that changes the tiles, so switching
 * Hybrid → Satellite → Hybrid reuses the first session rather than opening a
 * third.
 */
const sessionCache = new Map<SessionKey, Promise<GoogleSession>>();

interface SessionRequest {
  key: string;
  mapType: 'roadmap' | 'satellite' | 'terrain';
  layerTypes?: string[];
  language: string;
  region: string;
}

function sessionKeyOf(req: SessionRequest): SessionKey {
  return [req.mapType, (req.layerTypes ?? []).join('+'), req.language, req.region, req.key].join(
    '|',
  );
}

async function createSession(req: SessionRequest): Promise<GoogleSession> {
  const response = await fetch(
    `https://tile.googleapis.com/v1/createSession?key=${encodeURIComponent(req.key)}`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        mapType: req.mapType,
        language: req.language,
        region: req.region,
        ...(req.layerTypes ? { layerTypes: req.layerTypes } : {}),
        // Retina tiles. A tracking map is read at deep zoom on a laptop screen
        // and 1x labels are noticeably mushy there.
        highDpi: true,
        scale: 'scaleFactor2x',
      }),
    },
  );

  if (!response.ok) {
    const body = await response.text();
    throw new Error(
      `Google Map Tiles refused the session (${response.status}). ` +
        `Check the key is a browser key with the Map Tiles API enabled and this ` +
        `origin allowed. ${body.slice(0, 300)}`,
    );
  }

  const body = (await response.json()) as { session: string; expiry: string };
  return { session: body.session, expiry: Number(body.expiry) };
}

function getSession(req: SessionRequest): Promise<GoogleSession> {
  const cacheKey = sessionKeyOf(req);
  const existing = sessionCache.get(cacheKey);
  if (existing) return existing;

  const promise = createSession(req).catch((error: unknown) => {
    // Not cached on failure: a key fixed in Settings must work on the next
    // render rather than after a reload.
    sessionCache.delete(cacheKey);
    throw error;
  });
  sessionCache.set(cacheKey, promise);
  return promise;
}

/** Maps a basemap id onto the Map Tiles session parameters it needs. */
function googleParamsFor(
  id: BasemapId,
): { mapType: SessionRequest['mapType']; layerTypes?: string[] } | null {
  switch (id) {
    case 'google_roadmap':
      return { mapType: 'roadmap' };
    case 'google_satellite':
      return { mapType: 'satellite' };
    case 'google_hybrid':
      // Hybrid is not a map type — it is satellite imagery with the roadmap
      // vector layer composited on top.
      return { mapType: 'satellite', layerTypes: ['layerRoadmap'] };
    case 'google_terrain':
      return { mapType: 'terrain', layerTypes: ['layerRoadmap'] };
    default:
      return null;
  }
}

/**
 * A Google basemap, with the attribution Google's terms require.
 *
 * The copyright line is not static — it depends on who supplied the imagery for
 * the area on screen, so Google serves it per viewport and it legitimately
 * changes as you pan across a border. It is fetched on `moveend`, debounced,
 * and pushed into Leaflet's own attribution control rather than into the
 * TileLayer's `attribution` prop: changing that prop would remount the layer
 * and re-request every visible tile on every pan.
 */
export function GoogleBasemap({
  basemap,
  settings,
  maxZoom,
  onError,
}: {
  basemap: BasemapId;
  settings: MapSettings;
  maxZoom: number;
  onError: (message: string | null) => void;
}) {
  const map = useMap();
  const [session, setSession] = useState<string | null>(null);
  const [copyright, setCopyright] = useState<string>('© Google');

  const params = googleParamsFor(basemap);
  const key = settings.googleMapsBrowserKey;
  const definition = basemapById(basemap);

  useEffect(() => {
    // Derived INSIDE the effect rather than closed over from the render body:
    // `googleParamsFor` returns a fresh object every render, so depending on it
    // would re-create the session on every keystroke elsewhere in the page. The
    // basemap id it comes from is the real dependency.
    const request = googleParamsFor(basemap);
    if (!request || !key) return;

    let cancelled = false;
    setSession(null);
    onError(null);

    void getSession({
      key,
      mapType: request.mapType,
      ...(request.layerTypes ? { layerTypes: request.layerTypes } : {}),
      language: settings.labelLanguage,
      region: settings.labelRegion,
    })
      .then((s) => {
        if (!cancelled) setSession(s.session);
      })
      .catch((error: unknown) => {
        if (!cancelled) onError(error instanceof Error ? error.message : String(error));
      });

    return () => {
      cancelled = true;
    };
  }, [basemap, key, settings.labelLanguage, settings.labelRegion, onError]);

  // Viewport copyright, debounced against pan and zoom.
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!session || !key) return;

    const refresh = () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        const b = map.getBounds();
        const url =
          `https://tile.googleapis.com/tile/v1/viewport?session=${encodeURIComponent(session)}` +
          `&key=${encodeURIComponent(key)}&zoom=${map.getZoom()}` +
          `&north=${b.getNorth()}&south=${b.getSouth()}&east=${b.getEast()}&west=${b.getWest()}`;
        void fetch(url)
          .then((r) => (r.ok ? (r.json() as Promise<{ copyright?: string }>) : null))
          .then((body) => {
            if (body?.copyright) setCopyright(body.copyright);
          })
          .catch(() => {
            // Attribution is required, so a failed lookup keeps the generic
            // '© Google' rather than clearing the line.
          });
      }, 600);
    };

    refresh();
    map.on('moveend', refresh);
    return () => {
      map.off('moveend', refresh);
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [session, key, map]);

  useEffect(() => {
    const control = map.attributionControl;
    if (!control) return;
    control.addAttribution(copyright);
    return () => {
      control.removeAttribution(copyright);
    };
  }, [map, copyright]);

  if (!session || !key || !params) return null;

  return (
    <TileLayer
      url={`https://tile.googleapis.com/v1/2dtiles/{z}/{x}/{y}?session=${encodeURIComponent(
        session,
      )}&key=${encodeURIComponent(key)}`}
      maxZoom={maxZoom}
      maxNativeZoom={definition.maxNativeZoom}
      // Google serves 512px retina tiles under scaleFactor2x; telling Leaflet
      // they are 256 would draw them at double scale and halve the effective
      // resolution the deep-zoom setting exists to buy.
      tileSize={512}
      zoomOffset={-1}
      // Attribution is supplied through the control above, per-viewport.
      attribution=""
    />
  );
}

/**
 * The basemap layer for the current selection.
 *
 * One component so the map body does not have to branch, and so switching
 * basemaps unmounts the previous tile layer cleanly — leaving both mounted was
 * how an early version ended up with satellite imagery permanently under a
 * half-transparent street map.
 */
export function Basemap({
  basemap,
  settings,
  maxZoom,
  onGoogleError,
}: {
  basemap: BasemapId;
  settings: MapSettings;
  maxZoom: number;
  onGoogleError: (message: string | null) => void;
}) {
  if (isGoogleBasemap(basemap)) {
    return (
      <GoogleBasemap
        basemap={basemap}
        settings={settings}
        maxZoom={maxZoom}
        onError={onGoogleError}
      />
    );
  }

  if (basemap === 'satellite') {
    return (
      <TileLayer
        attribution="Imagery &copy; Esri, Maxar, Earthstar Geographics"
        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        maxZoom={maxZoom}
        maxNativeZoom={19}
      />
    );
  }

  return (
    <TileLayer
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      maxZoom={maxZoom}
      maxNativeZoom={19}
    />
  );
}

/** Remembers the basemap per browser, so it survives a reload. */
export function useStoredBasemap(fallback: BasemapId, hasGoogleKey: boolean) {
  const [basemap, setBasemap] = useState<BasemapId>(fallback);
  const touched = useRef(false);

  useEffect(() => {
    if (touched.current) return;
    let stored: string | null = null;
    try {
      stored = window.localStorage.getItem('gt-basemap');
    } catch {
      // Private mode, or storage disabled. The company default stands.
    }
    const candidate = BASEMAPS.find((b) => b.id === stored)?.id ?? fallback;
    setBasemap(resolveBasemap(candidate, hasGoogleKey));
  }, [fallback, hasGoogleKey]);

  const choose = useMemo(
    () => (id: BasemapId) => {
      touched.current = true;
      setBasemap(id);
      try {
        window.localStorage.setItem('gt-basemap', id);
      } catch {
        // Not worth surfacing: the choice still applies for this session.
      }
    },
    [],
  );

  return [basemap, choose] as const;
}
